import type { CreditRefillStatus } from '../services/subscriptionService';

function validTimeZone(timeZone: string): string | undefined {
  try {
    new Intl.DateTimeFormat('en', { timeZone }).format();

    return timeZone;
  } catch {
    return undefined;
  }
}

function localDay(date: Date, timeZone: string | undefined): number {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find((value) => value.type === type)?.value);

  return Date.UTC(part('year'), part('month') - 1, part('day')) / 86_400_000;
}

function refillDay(date: Date, now: Date, timeZone: string | undefined): string {
  const distance = localDay(date, timeZone) - localDay(now, timeZone);

  if (distance === 0) return 'today';
  if (distance === 1) return 'tomorrow';

  return `on ${new Intl.DateTimeFormat(undefined, {
    timeZone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(date)}`;
}

/** Compare the server's next grant in the viewer's zone, as elsewhere in planning. */
// eslint-disable-next-line max-statements
export function formatCreditRefillNotice(
  status: CreditRefillStatus, now = new Date(), viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string | null {
  const grant = status.next_daily_credit_grant;

  if (!grant) return status.next_daily_credit_grant_reason === 'plan_not_eligible'
    ? 'Your current plan does not include daily credit refills. Upgrade or add credits now.' : null;
  const scheduled = new Date(grant.scheduled_at);

  if (!Number.isFinite(grant.amount) || grant.amount <= 0 || !Number.isFinite(scheduled.getTime())) return null;
  const credits = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(grant.amount);
  const plan = grant.plan_name || status.plan_name;

  if (grant.status === 'pending' || scheduled <= now) {
    return `Your ${plan} plan's next ${credits} credits are due and awaiting the daily refill. `
      + 'Check back shortly, or upgrade or add credits now.';
  }
  // Profile zones currently default to UTC without a settings UI. Prefer the
  // browser's actual zone, including travel and daylight-saving adjustments.
  const timeZone = validTimeZone(viewerTimeZone) || validTimeZone(status.timezone);
  const time = new Intl.DateTimeFormat(undefined, {
    timeZone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(scheduled);

  return `You'll get ${credits} more credits from your ${plan} plan ${refillDay(scheduled, now, timeZone)} `
    + `after ${time}. Come back then, or upgrade or add credits now.`;
}
